import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { Form, useForm } from "@portal/BaseComponents/Form/AntForm";
import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { Row, Col } from "@portal/BaseComponents/Grid/AntGrid";
import { Input } from "@portal/BaseComponents/Input/AntInput";
import { Modal } from "@portal/BaseComponents/Modal/AntModal";
import { Select } from "@portal/BaseComponents/Select/AntSelect";
import { SkeletonInput } from "@portal/BaseComponents/Skeleton/AntSkeleton";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { FormFooter } from "@portal/BaseComponents/UI/FormArea/FormFooter";
import { FormHeader } from "@portal/BaseComponents/UI/FormArea/FormHeader";
import { Icon } from "@portal/lib/icons/icons";

import {
  useCreateContactQuery,
  useGetContactTypes,
  useUpdateContactQuery,
} from "@portal/queries/contact-query";
import { userConfigAtom } from "@portal/store/userConfig/userConfig";
import {
  ContactTypes,
  Contact_LV_UI_DTO,
  CreateContact,
} from "@portal/types/entities/contact";
import { emailValidation } from "@portal/util/validation";

import produce from "immer";
import { useSetAtom } from "jotai";

interface AddNewItemModalProps {
  open: boolean;
  onClose: () => void;
  onOpenChange?: (open: boolean) => void;
  contact?: Contact_LV_UI_DTO;
  modalType: "ADD" | "EDIT";
}

const t = {
  warningText: "Either Email Address or Phone Number should be entered.",
  newContact: "New Contact",
  editContact: "Edit Contact",
};

const contactTypeOptionPredicate = (type: ContactTypes) => {
  return {
    label: type.display_text,
    value: type.id,
  };
};

function AddNewItemModal(props: AddNewItemModalProps) {
  const { open, onClose, modalType, contact } = props;
  const [form] = useForm<CreateContact>();
  const updateConfigAtom = useSetAtom(userConfigAtom);
  const { data, isLoading } = useGetContactTypes();
  const createCreate = useCreateContactQuery({
    onSuccessCallBack: handleSuccessCallback,
  });
  const updateContact = useUpdateContactQuery({
    onSuccessCallBack: handleSuccessCallback,
    id: contact?.id,
  });

  function handleSuccessCallback(data?: Contact_LV_UI_DTO) {
    onClose();
    if (!data) return;
    if (modalType === "ADD") {
      updateConfigAtom((config) => {
        return produce(config, (draft) => {
          draft.user_details?.customer?.contacts?.push(data);
          return draft;
        });
      });
    } else {
      updateConfigAtom((config) => {
        return produce(config, (draft) => {
          const index = config.user_details?.customer?.contacts?.findIndex(
            (f) => f.id === contact?.id
          ) as number;
          if (!draft.user_details?.customer.contacts) return draft;
          draft.user_details.customer.contacts[index] = data;
          return draft;
        });
      });
    }
  }
  function handleClose() {
    onClose();
  }

  function handleOpenChange() {
    handleClose();
  }

  const HeaderTitle = (
    <TypographyText fs="20">
      {modalType === "ADD" ? t.newContact : t.editContact}
    </TypographyText>
  );

  const Header = <FormHeader title={HeaderTitle} onClose={handleClose} />;

  function handleSubmit() {
    form.validateFields().then(() => {
      const values = form.getFieldsValue();
      if (modalType === "ADD") {
        createCreate.mutateAsync(values);
      } else {
        updateContact.mutateAsync(values);
      }
    });
  }

  const Footer = (
    <FormFooter
      cancelButtonProps={{ onClick: handleClose }}
      saveButtonProps={{
        onClick: handleSubmit,
        loading: createCreate.isLoading || updateContact.isLoading,
      }}
    />
  );

  const FirstAndLastNameArea = (
    <Row gutter={[16, 0]}>
      <Col span={12}>
        <StyledFormItem
          label="First Name"
          space="sp-1"
          preserve={false}
          name="first_name"
          initialValue={contact?.first_name}
        >
          <Input placeholder="Enter First Name" />
        </StyledFormItem>
      </Col>
      <Col span={12}>
        <StyledFormItem
          label="Last Name"
          space="sp-1"
          preserve={false}
          name="last_name"
          initialValue={contact?.last_name}
        >
          <Input placeholder="Enter Last Name" />
        </StyledFormItem>
      </Col>
    </Row>
  );

  const EmailArea = (
    <StyledFormItem
      label="Email Address"
      space="sp-1"
      preserve={false}
      name="email"
      rules={[
        { required: true, message: "Email Address is required" },
        ...emailValidation,
      ]}
      initialValue={contact?.email}
    >
      <Input placeholder="Enter a Email Address" />
    </StyledFormItem>
  );

  const ContactTypeArea = (
    <StyledFormItem
      label="Contact Type"
      space="sp-1"
      style={{ maxWidth: "300px" }}
      preserve={false}
      name="type_id"
      rules={[
        {
          required: true,
          message: "Contact Type is required",
        },
      ]}
    >
      {data && !isLoading ? (
        <Select
          options={data.list.map(contactTypeOptionPredicate)}
          placeholder="Select Contact Type"
        />
      ) : (
        <SkeletonInput />
      )}
    </StyledFormItem>
  );

  const PhoneNumberArea = (
    <StyledFormItem
      label="Phone Number"
      space="sp-1"
      style={{ maxWidth: "300px" }}
      preserve={false}
      name="phone_number"
      initialValue={contact?.phone_number}
    >
      <Input placeholder="Enter a Phone Number" />
    </StyledFormItem>
  );

  const WarningArea = (
    <Flex align="center">
      <Icon icon={["far", "info-circle"]} color={"var(--blue-6)"} />
      <TypographyText>{t.warningText}</TypographyText>
    </Flex>
  );

  return (
    <Modal
      open={open}
      width={640}
      closable={false}
      onCancel={handleOpenChange}
      title={Header}
      footer={Footer}
      centered
      destroyOnClose
      getContainer={false}
      styles={{
        body: {
          overflow: "auto",
          maxHeight: "600px",
        },
      }}
    >
      <Form layout="vertical" form={form} preserve={false}>
        {FirstAndLastNameArea}
        {EmailArea}
        {ContactTypeArea}
        {PhoneNumberArea}
        {WarningArea}
      </Form>
    </Modal>
  );
}

export default AddNewItemModal;
